import React, { useContext, useMemo, useState } from 'react';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { t } from '../../../../../../types/translation/Translator';
import Table from '../../../../../components/Table';
import { HandlingUnitContext } from '../../../../../../context/HandlingUnitContext';
import SearchBarWithFilter from '../../../../../../VentoryUI/components/common/SearchBarWithFilter';
import { ProductMasterDataContext } from '../../../../../../context/ProductMasterDataContext';
import { HandlingUnit } from '../../../../../../types/handling_unit/handlingUnit';
import { TagRelationContext } from '../../../../../../context/TagRelationContext';
import { CustomFieldContext } from '../../../../../../context/CustomFieldContext';
import { HandlingUnitFilter } from '../../../../../../VentoryUI/components/filters/HandlingUnitFilter/HandlingUnitFilter';
import { CreateButton } from '../../../../../components/Button';
import CreateHandlingUnitModal from './Modals/CreateHandlingUnitModal';
import UpdateHandlingUnitModal from './Modals/UpdateHandlingUnitModal';
import { DeleteIconButton } from '../../../../../icons';
import DeleteHandlingUnitModal from './Modals/DeleteHandlingUnitModal';
import { Checkbox } from '../../../../../components/Checkbox';
import { CompanyContext } from '../../../../../../context/CompanyContext';

interface HandlingUnitsOverviewPaneProps {
  pmdId?: string;
}

export default function HandlingUnitsOverviewPane({ pmdId }: HandlingUnitsOverviewPaneProps) {
  const { productMasterData, productMasterDataLoading } = useContext(ProductMasterDataContext);
  const { handlingUnits, handlingUnitsLoading } = useContext(HandlingUnitContext);
  const { tagRelations } = useContext(TagRelationContext);
  const { customFields } = useContext(CustomFieldContext);
  const { currentCompany } = useContext(CompanyContext);

  const allItems = useMemo(() => {
    return [...handlingUnits.values()].filter(
      item => !pmdId || item.productMasterDataId === pmdId || item.parentId === pmdId,
    );
  }, [handlingUnits]);

  const [item, setItem] = useState<HandlingUnit | undefined>();
  const [items, setItems] = useState<HandlingUnit[]>(allItems);

  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [updateModalOpen, setUpdateModalOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const headers = [
    {
      key: 'parentName',
      name: t(TranslationKey.handlingUnit).singular.label,
      text: (item: HandlingUnit) =>
        productMasterData.get(handlingUnits.get(item.id)?.parentId || '')?.productName || '',
    },
    {
      key: 'parentNumber',
      name: t(TranslationKey.number).singular.label,
      text: (item: HandlingUnit) =>
        productMasterData.get(handlingUnits.get(item.id)?.parentId || '')?.productNumber || '',
    },
    {
      key: 'sku',
      name: t(TranslationKey.stockKeepingUnit).singular.label,
      text: (item: HandlingUnit) => <Checkbox value={item.default} disabled={true} onChange={value => {}} />,
    },
    {
      key: 'quantity',
      name: t(TranslationKey.quantity).singular.label,
      text: (item: HandlingUnit) => item.quantity,
    },
    {
      key: 'delete',
      name: '',
      text: (item: HandlingUnit) => (
        <DeleteIconButton
          onClick={() => {
            setDeleteModalOpen(true);
            setItem(item);
          }}
        />
      ),
    },
  ];

  const filter = new HandlingUnitFilter(productMasterData, tagRelations, customFields, currentCompany);

  return (
    <>
      {createModalOpen && <CreateHandlingUnitModal open={createModalOpen} setOpen={setCreateModalOpen} pmdId={pmdId} />}
      {updateModalOpen && <UpdateHandlingUnitModal open={updateModalOpen} setOpen={setUpdateModalOpen} item={item} />}
      {deleteModalOpen && <DeleteHandlingUnitModal open={!!deleteModalOpen} setOpen={setDeleteModalOpen} item={item} />}

      <div className={`${pmdId ? '' : 'content-x'} flex h-full flex-grow flex-col gap-4`}>
        <div className='grid gap-2'>
          <p className='page-title'>{t(TranslationKey.handlingUnit).plural.upper}</p>
          <p className='subtitle'>{t(TranslationKey.handlingUnitScreenSubtitle).singular.upper}</p>
        </div>
        <div className='flex justify-between gap-4'>
          <SearchBarWithFilter
            loading={handlingUnitsLoading || productMasterDataLoading}
            items={allItems}
            setItems={setItems}
            placeholder={t(TranslationKey.handlingUnitFilter).singular.label}
            filter={filter}
          />
          <div className='flex'>
            <CreateButton onClick={() => setCreateModalOpen(true)} />
          </div>
        </div>
        <Table
          headers={headers}
          items={items}
          filter={filter}
          onClick={item => {
            setItem(item);
            setUpdateModalOpen(true);
          }}
        />
      </div>
    </>
  );
}
